import { InfoCircleFilled } from '@ant-design/icons';
import { Form, Input } from 'antd';
import type { InputRef } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { getUserOrganizationAccess } from '../../api/organization';
import { createWorkspace } from '../../api/workspace';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import UnionBg from '../../assets/icons/union-bg.svg';
import { getCurrentlyActiveOrganization } from '../../common/auth';
import { FrameworkCard } from '../../components/Cards/FrameworkCard';
import { SpaceVisibilityCard } from '../../components/Cards/SpaceVisibilityCard';
import { BaseButton } from '../../components/UI/BaseButton';
import { queryClient } from '../../queryClient';
import { WorkspaceFramework } from '../../types/Workspace';
import { WORKSPACE_VISIBILITY } from '../Onboarding/WorkspaceOnboarding';

export type newSpaceFormValues = {
  spaceName: string;
  spaceVisibility: WORKSPACE_VISIBILITY;
  spaceFramework: WorkspaceFramework;
};

export default function NewSpace() {
  const [newSpaceForm] = Form.useForm();
  const spaceNameInputRef = useRef<InputRef>(null);
  const navigate = useNavigate();

  const [selectedVisibility, setSelectedVisibility] = useState<WORKSPACE_VISIBILITY>(WORKSPACE_VISIBILITY.Public);
  const [selectedFramework, setSelectedFramework] = useState<WorkspaceFramework>(WorkspaceFramework.scrum);

  // Watch spaceName field to enable/disable button
  const spaceName = Form.useWatch('spaceName', newSpaceForm);
  const hasValidName = spaceName?.trim().length > 0;

  // Auto-focus teamspace name input on mount
  useEffect(() => {
    if (spaceNameInputRef.current) {
      spaceNameInputRef.current.focus();
    }
  }, []);

  const createNewSpace = async (values: newSpaceFormValues) => {
    // Access the newSpaceForm values
    const res = await createWorkspace(values.spaceName, values.spaceVisibility, values.spaceFramework);

    if (res.status < 200 || res.status > 299) {
      console.log('Error creating new space');
      return;
    }

    // Update state to force rerender of sidebar
    const organization = await getCurrentlyActiveOrganization();

    // Refetch queries before navigation to ensure fresh data is loaded
    await queryClient.refetchQueries({ queryKey: ['workspaces', organization.id] });

    // Prime — not refetch — the org-accesses cache: this screen lives outside BaseIndex, so
    // the query has no observer here and refetchQueries is a no-op whenever the entry is
    // missing or has been garbage collected. BaseIndex resolves the space's org from it and
    // sends the user to the org picker when it can't, which is how a member who had just
    // created their first space landed on the Org screen instead of that space (#2987).
    const accesses = await getUserOrganizationAccess();
    queryClient.setQueryData(['usersOrganizationAccess'], accesses.data);

    // analytics
    analytics.track(AnalyticsEvent.spaceCreated, {
      spaceId: res.data.id,
      organizationId: organization.id,
    });

    // Always offer the invite step on creation; public spaces no longer auto-add members, so the
    // creator seeds the initial members here (the step can be skipped).
    navigate(`/${organization.publicId}/space/${res.data.publicId}/new-space-invite`);
  };

  return (
    <div
      className="flex justify-center items-center min-h-screen bg-brand-white"
      style={{
        backgroundImage: `url(${UnionBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="flex flex-col w-[656px] max-h-[90vh] bg-brand-white border-2 border-solid border-taskonioGreen-600 rounded-[12px] py-6 px-10 relative shadow-[0_0_0_4px_#7ff5aa,0_4px_16px_0_rgba(112,119,146,0.4)] overflow-y-auto">
        <div className="flex flex-col items-center justify-center gap-5 my-6">
          <LogoFullIcon />
          <div className="text-brand-grey-dark text-base font-semibold">Setting up New Space</div>
        </div>

        <h1 className="font-semibold text-xl text-brand-grey-dark mb-4 text-left">Create Teamspace</h1>

        <div className="bg-neutral-200 rounded-[4px] p-3 mb-6 flex gap-2">
          <InfoCircleFilled className="text-neutral-800 text-base mt-0.5 flex-shrink-0" />
          <div className="text-neutral-900 text-xs leading-4">
            A Space is where your team builds together. Keep your backlog, sprints, and tasks organized, and move from
            idea to release, all in one place.
          </div>
        </div>

        <Form
          form={newSpaceForm}
          layout="vertical"
          name="newSpaceForm"
          initialValues={{ spaceVisibility: WORKSPACE_VISIBILITY.Public, spaceFramework: WorkspaceFramework.scrum }}
        >
          <Form.Item
            name="spaceName"
            label={<div className="text-brand-grey-dark text-xs font-semibold">Teamspace name</div>}
            required={false}
            rules={[
              {
                required: true,
                message: 'Teamspace name can not be empty',
              },
            ]}
          >
            <Input ref={spaceNameInputRef} placeholder="Type Teamspace name" className="h-10" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="spaceVisibility"
            className="mb-4"
            label={<div className="text-brand-grey-dark text-xs font-semibold">Teamspace visibility</div>}
          >
            <div className="flex gap-2">
              <SpaceVisibilityCard
                visibility={WORKSPACE_VISIBILITY.Public}
                selected={selectedVisibility === WORKSPACE_VISIBILITY.Public}
                current={false}
                onSelected={() => setSelectedVisibility(WORKSPACE_VISIBILITY.Public)}
              />

              <SpaceVisibilityCard
                visibility={WORKSPACE_VISIBILITY.Private}
                selected={selectedVisibility === WORKSPACE_VISIBILITY.Private}
                current={false}
                onSelected={() => setSelectedVisibility(WORKSPACE_VISIBILITY.Private)}
              />
            </div>
          </Form.Item>

          <Form.Item
            name="spaceFramework"
            className="mb-2"
            label={
              <div className="flex gap-1">
                <div className="text-brand-grey-dark text-xs font-semibold">Framework</div>
                <div className="text-neutral-800 text-[10px] font-medium leading-3">
                  (you can always change this later)
                </div>
              </div>
            }
          >
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <FrameworkCard
                  framework={WorkspaceFramework.scrum}
                  selected={selectedFramework === WorkspaceFramework.scrum}
                  onSelected={() => setSelectedFramework(WorkspaceFramework.scrum)}
                  current={false}
                />

                <FrameworkCard
                  framework={WorkspaceFramework.kanban}
                  selected={selectedFramework === WorkspaceFramework.kanban}
                  onSelected={() => setSelectedFramework(WorkspaceFramework.kanban)}
                  current={false}
                />
              </div>

              <FrameworkCard
                framework={WorkspaceFramework.none}
                selected={selectedFramework === WorkspaceFramework.none}
                onSelected={() => setSelectedFramework(WorkspaceFramework.none)}
                current={false}
              />
            </div>
          </Form.Item>

          <div className="flex justify-end mt-8">
            <BaseButton
              type="primary"
              size="large"
              disabled={!hasValidName}
              onClick={() => {
                newSpaceForm.setFieldValue('spaceFramework', selectedFramework);
                newSpaceForm.setFieldValue('spaceVisibility', selectedVisibility);
                newSpaceForm
                  .validateFields()
                  .then((values) => {
                    createNewSpace(values as newSpaceFormValues)
                      .then((r) => console.log(r))
                      .catch((e) => console.log(e));
                  })
                  .catch((info) => {
                    console.log('Validate Failed:', info);
                  });
              }}
            >
              Create Teamspace
            </BaseButton>
          </div>
        </Form>
      </div>
    </div>
  );
}
